/**
 * LOXORD motion engine
 * Smooth scroll (Lenis) · snap world · idle drift · world colour · reveals ·
 * menu tablet · policy accordion
 *
 * The world moves slowly. All ambient motion is disabled by the theme's
 * master switch or the visitor's reduced-motion preference — with one
 * exception: on touch devices the homepage's snap scrolling stays, because
 * snapping is navigation, not decoration.
 */
(() => {
  const config = window.LoxordMotion || {};
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = matchMedia('(hover: hover) and (pointer: fine)');
  const coarsePointer = matchMedia('(pointer: coarse)');
  const phoneLandscape = matchMedia('(pointer: coarse) and (orientation: landscape) and (max-height: 500px)');
  const motionAllowed = () => config.enabled && !reducedMotion.matches;

  /* -------------------------------------------------------------------
   * Smooth scroll — the document scroller, desktop only.
   * On touch the document does not scroll at all on the homepage, and a
   * window-level Lenis there would only fight the snap world's own.
   * Product rooms lock the document on every device — a window Lenis there
   * would swallow the wheel events the card's own scroll area needs.
   * ----------------------------------------------------------------- */
  let lenis = null;

  function startLenis() {
    if (lenis || coarsePointer.matches) return;
    if (document.documentElement.classList.contains('room-world')) return;
    if (!motionAllowed() || !config.smoothScroll || typeof Lenis === 'undefined') return;
    lenis = new Lenis({ smoothWheel: true, lerp: config.scrollLerp || 0.1 });
    const raf = (t) => {
      if (!lenis) return;
      lenis.raf(t);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }

  function stopLenis() {
    lenis?.destroy();
    lenis = null;
  }

  /* -------------------------------------------------------------------
   * The world's colour — four clouds, every page, one recolour path.
   *
   * A cloud is one solid colour, so recolouring is a single assignment. There
   * is no alpha ramp to reproduce here: the softness comes from the blur on the
   * field, and the mixing from blurring the composite. The old four-stop ramp
   * had to be kept identical in Liquid and in this file, and keeping those two
   * copies in agreement is what broke the world twice.
   * ----------------------------------------------------------------- */
  function paintClouds(atmosphere, colours) {
    if (!atmosphere) return;
    // the CSS field first, so the fallback stays coherent whatever happens
    atmosphere.querySelectorAll('[data-cloud-slot]').forEach((cloud, i) => {
      const colour = (colours[i] || '').trim();
      // a blank slot keeps the colour it wears rather than going transparent
      if (colour) cloud.style.setProperty('--cloud', colour);
    });
    // then the shader, with the same eased duration the CSS vars use
    window.LoxordMesh?.set(atmosphere, colours, config.shiftMs || 700);
  }

  /* Resting is a repaint, never a removal. A cloud's colour lives only in the
     inline style Liquid wrote, and --cloud's registered initial value is
     `transparent`, so removeProperty would not rest the world — it would erase
     it. The snippet hands us the resolved resting colours for exactly this. */
  function restClouds(atmosphere) {
    if (!atmosphere) return;
    paintClouds(atmosphere, (atmosphere.dataset.restClouds || '').split('|'));
  }

  const tileClouds = (tile) => (tile.dataset.clouds || '').split('|');

  /* -------------------------------------------------------------------
   * Idle drift — one shared loop, per-tile phase, on-screen tiles only
   * ----------------------------------------------------------------- */
  const drifters = new Map(); // el -> {phase, speed, ampX, ampY}
  const visible = new Set();
  let driftRunning = false;

  const driftObserver = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      }
    },
    { rootMargin: '20%' }
  );

  let lastBeat = 0;

  function driftLoop(t) {
    if (!driftRunning) return;
    for (const [el, d] of drifters) {
      if (!visible.has(el)) continue;
      el.style.setProperty('--offset-x', `${Math.sin(t * d.speed + d.phase) * d.ampX}px`);
      el.style.setProperty('--offset-y', `${Math.sin(t * d.speed * 0.7 + d.phase) * d.ampY}px`);
    }
    // heartbeat: lets us verify from outside that the loop is alive
    if (t - lastBeat > 1000) {
      lastBeat = t;
      document.documentElement.dataset.loxordDrift = String(Math.round(t));
    }
    requestAnimationFrame(driftLoop);
  }

  function addDrifter(el) {
    const amp = Number(el.dataset.driftAmp) || config.floatAmplitude || 8;
    const speedFactor = Number(el.dataset.driftSpeedFactor) || 1;
    const speed = (config.floatSpeed ?? 4) * 0.00004 * speedFactor; // rad/ms — full sway ≈ 40s at default
    drifters.set(el, {
      phase: Math.random() * Math.PI * 2,
      speed: speed * (0.8 + Math.random() * 0.4),
      ampX: amp * (0.75 + Math.random() * 0.5),
      ampY: amp * 0.35 * (0.75 + Math.random() * 0.5), // vertical much smaller — horizontal-biased float
    });
    driftObserver.observe(el);
    if (motionAllowed() && !driftRunning) {
      driftRunning = true;
      requestAnimationFrame(driftLoop);
    }
  }

  function removeDrifter(el) {
    drifters.delete(el);
    visible.delete(el);
    driftObserver.unobserve(el);
  }

  function stopDrift() {
    driftRunning = false;
    for (const el of drifters.keys()) {
      el.style.removeProperty('--offset-x');
      el.style.removeProperty('--offset-y');
    }
  }

  /* -------------------------------------------------------------------
   * The snap world — touch devices, homepage only.
   *
   * The section renders its objects into a fixed, full-viewport scroller;
   * Lenis drives it with syncTouch, and a DIRECTIONAL settler (see below)
   * finishes every gesture on a cell centre — always forward in the
   * direction of travel, never pulling back past something already crossed.
   *
   * Distance blur and scale are computed here rather than with CSS
   * scroll-driven animations: those are Safari 26+, and the audience is on
   * everything back to iPhone 8.
   * ----------------------------------------------------------------- */
  const BLUR_MAX = 6; // px, at a full viewport-half away
  const SCALE_MAX = 0.06;
  const DEAD_ZONE = 0.25; // the centred object stays perfectly sharp
  const BLUR_STEP = 0.5; // quantised: sub-pixel blur changes cost a pass and show nothing
  const SCALE_STEP = 0.005;
  const NEIGHBOURS = 2; // cells either side of centre that can be on screen

  const snapWorlds = new Map(); // section root -> { focusCell }

  function initSnapWorld(section) {
    const scroller = section.querySelector('[data-fmg-scroller]');
    const grid = scroller?.firstElementChild;
    if (!scroller || !grid) return null;
    // The stylesheet decides whether this is a snap world; trust it rather than
    // re-deriving the media queries here.
    if (getComputedStyle(scroller).position !== 'fixed') return null;

    // The colonnade is a tail, not an exhibit: it never becomes a snap
    // target, and the trailing pad shrinks by its height so the LAST OBJECT
    // stays the final resting point, marks standing in the band beneath it.
    const colonnade = grid.querySelector('.fmg__colonnade');
    const cells = [...grid.children].filter((cell) => cell !== colonnade);
    if (!cells.length) return null;

    const atmosphere = section.querySelector('.atmosphere');
    const metrics = cells.map((cell) => ({
      cell,
      media: cell.querySelector('.fmg__media'),
      tile: cell.querySelector('[data-gradient-start]'),
      center: 0,
      blur: -1,
      scale: -1,
    }));

    let engine = null;
    let viewportHeight = window.innerHeight;
    let currentIndex = -1;

    const setPad = (name, px) => {
      const value = `${Math.round(px)}px`;
      if (grid.style.getPropertyValue(name) !== value) grid.style.setProperty(name, value);
    };

    /* Cells are content-height, not a flat fraction of the viewport, so the
       leading and trailing padding that lets the first and last object reach
       dead centre has to be measured rather than expressed in CSS. */
    const measure = () => {
      viewportHeight = window.innerHeight;
      setPad('--pad-start', Math.max(0, (viewportHeight - cells[0].offsetHeight) / 2));
      setPad(
        '--pad-end',
        Math.max(0, (viewportHeight - cells[cells.length - 1].offsetHeight) / 2 - (colonnade?.offsetHeight || 0))
      );
      for (const m of metrics) m.center = m.cell.offsetTop + m.cell.offsetHeight / 2;
    };

    const paint = (m, effect) => {
      const blur = Math.round((effect * BLUR_MAX) / BLUR_STEP) * BLUR_STEP;
      const scale = 1 - Math.round((effect * SCALE_MAX) / SCALE_STEP) * SCALE_STEP;
      if (blur === m.blur && scale === m.scale) return;
      m.blur = blur;
      m.scale = scale;
      if (!m.media) return;
      // a 0px blur is still a filter pass — hard-clear it instead
      m.media.style.filter = blur === 0 ? 'none' : `blur(${blur}px)`;
      m.media.style.transform = scale === 1 ? 'translateZ(0)' : `translateZ(0) scale(${scale})`;
    };

    const clearPaint = () => {
      for (const m of metrics) {
        m.blur = -1;
        m.scale = -1;
        m.media?.style.removeProperty('filter');
        m.media?.style.removeProperty('transform');
      }
    };

    /* The world takes the colours of whatever is centred, and keeps them:
       resting the gradient between snaps would flash the base colours on
       every single scroll. */
    const paintWorld = (index) => {
      const tile = metrics[index]?.tile;
      if (!tile || !atmosphere) return;
      atmosphere.style.setProperty('--g1', tile.dataset.gradientStart);
      atmosphere.style.setProperty('--g2', tile.dataset.gradientEnd || tile.dataset.gradientStart);
      paintClouds(atmosphere, tileClouds(tile));
    };

    const onScroll = () => {
      const scroll = engine ? engine.animatedScroll : scroller.scrollTop;
      const half = viewportHeight / 2 || 1;
      const eye = scroll + half;

      let nearest = 0;
      let nearestDist = Infinity;
      for (let i = 0; i < metrics.length; i += 1) {
        const dist = Math.abs(metrics[i].center - eye);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }
      }

      if (motionAllowed()) {
        for (let i = 0; i < metrics.length; i += 1) {
          // anything further out than a couple of cells cannot be on screen:
          // park it at full effect once and stop writing to it
          const effect =
            Math.abs(i - nearest) <= NEIGHBOURS
              ? Math.min(1, Math.max(0, (Math.abs(metrics[i].center - eye) / half - DEAD_ZONE) / (1 - DEAD_ZONE)))
              : 1;
          paint(metrics[i], effect);
        }
      }

      if (nearest !== currentIndex) {
        currentIndex = nearest;
        paintWorld(nearest);
      }

      if (settleTarget !== -1 && Math.abs(metrics[settleTarget].center - eye) < 1) {
        armed = false;
        settleTarget = -1;
      } else {
        settle();
      }
    };

    if (typeof Lenis !== 'undefined') {
      engine = new Lenis({
        wrapper: scroller,
        content: grid,
        syncTouch: true,
        syncTouchLerp: 0.075,
        touchInertiaExponent: 1.7,
        gestureOrientation: 'vertical',
        overscroll: false,
        autoRaf: true,
      });
    }

    /* DIRECTIONAL settle, replacing nearest-cell snapping. Nearest-cell pulls
       a strong flick BACKWARD to whatever it overshot — which reads as the
       world snatching the object away. Instead: once the finger is up and the
       inertia has decayed, glide FORWARD (in the direction of travel) to the
       next centre — never backward. A harsh flick may land several objects
       on; a gentle one lands the next; motion is always one continuous
       forward run. */
    let touching = false;
    let armed = false;
    let settleTarget = -1;

    const settle = () => {
      if (!engine || touching || !armed || settleTarget !== -1) return;
      if (Math.abs(engine.velocity) >= 18) return; // still flying — wait
      const eye = engine.animatedScroll + viewportHeight / 2;
      let nearest = 0;
      let nearestDist = Infinity;
      for (let i = 0; i < metrics.length; i += 1) {
        const dist = Math.abs(metrics[i].center - eye);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }
      }
      const tolerance = viewportHeight * 0.06;
      const dir = engine.direction || 0;
      let idx = nearest;
      if (dir > 0 && metrics[nearest].center < eye - tolerance) idx = Math.min(nearest + 1, metrics.length - 1);
      else if (dir < 0 && metrics[nearest].center > eye + tolerance) idx = Math.max(nearest - 1, 0);
      settleTarget = idx;
      engine.scrollTo(metrics[idx].center - viewportHeight / 2, { lerp: 0.09 });
    };

    const onTouchStart = () => {
      touching = true;
      armed = false;
      settleTarget = -1;
    };
    const onTouchEnd = () => {
      touching = false;
      armed = true;
      settleTarget = -1;
      // a slow drag can end with no inertia at all — no further scroll
      // events would ever fire, so ask once ourselves
      setTimeout(settle, 90);
    };
    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchend', onTouchEnd, { passive: true });

    measure();

    if (engine) engine.on('scroll', onScroll);
    else scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    const onResize = () => {
      measure();
      onScroll();
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    // snap stays either way; blur and scale are decoration and must let go
    const onMotionPref = () => {
      if (!motionAllowed()) clearPaint();
      onScroll();
    };
    reducedMotion.addEventListener('change', onMotionPref);

    // images arrive after first paint and change cell heights with them
    const gridObserver = new ResizeObserver(onResize);
    gridObserver.observe(grid);

    const focusCell = (cell) => {
      armed = false;
      settleTarget = -1;
      const offset = -Math.max(0, (window.innerHeight - cell.offsetHeight) / 2);
      if (engine) engine.scrollTo(cell, { offset });
      else scroller.scrollTo({ top: cell.offsetTop + offset, behavior: 'smooth' });
    };

    snapWorlds.set(section, { focusCell });

    return () => {
      snapWorlds.delete(section);
      gridObserver.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      reducedMotion.removeEventListener('change', onMotionPref);
      scroller.removeEventListener('touchstart', onTouchStart);
      scroller.removeEventListener('touchend', onTouchEnd);
      if (engine) {
        engine.off('scroll', onScroll);
        engine.destroy();
      } else {
        scroller.removeEventListener('scroll', onScroll);
      }
      grid.style.removeProperty('--pad-start');
      grid.style.removeProperty('--pad-end');
      clearPaint();
      atmosphere?.style.removeProperty('--g1');
      atmosphere?.style.removeProperty('--g2');
      restClouds(atmosphere);
    };
  }

  /* -------------------------------------------------------------------
   * The object's room — the photo column, product pages.
   *
   * Two bodies, one soul (skylrk.com's own pair, ported exactly):
   *   · HAND — the stylesheet lays the strip out horizontally: slides 85%
   *     wide, native momentum + scroll-snap, and skylrk's pull — a photo
   *     leaving focus shrinks toward half size and blurs toward 10px
   *     (l = 1 − 0.52·d in slide-widths; scale = clamp(l, .5, 1);
   *     blur = clamp((1−l)·15, 0, 10)).
   *   · DESK — the strip turns vertical and Lenis drives it with the
   *     homepage's own focus physics; the cell is exactly the view, no peek.
   * The stylesheet decides which body; this engine reads the strip's
   * flex-direction and drives whichever it finds, rebuilding if a resize
   * crosses the boundary. Dots and thumbs stay the direct switchers.
   * ----------------------------------------------------------------- */
  const SK_PULL = 0.52; // skylrk: per-slide-distance factor
  const SK_SCALE_MIN = 0.5;
  const SK_BLUR_MAX = 10;

  function initPdpCarousel(root) {
    const view = root.querySelector('[data-pdp-view]');
    const strip = view && view.querySelector('[data-pdp-strip]');
    if (!view || !strip || strip.children.length < 2) return () => {};

    const slides = [...strip.children];
    const metrics = slides.map((slide) => ({
      slide,
      img: slide.querySelector('img'),
      center: 0,
      blur: -1,
      scale: -1,
    }));
    const controls = [...root.querySelectorAll('[data-pdp-goto]')];
    let current = -1;

    /* THE BRIDGE — hands only. The page scroller (card) covers the whole
       viewport and owns every touch; the photo column lives beneath it on
       the fixed stage, so the browser can never chain gestures to it. So:
       touches that start on the card or the dots stay fully native (the card
       scrolls from anywhere); touches on the open photo area whose axis
       matches the carousel are forwarded to the column by hand, and settle
       DIRECTIONALLY on a slide centre — same law as the homepage, never
       backward. */
    const pageScroll = root.querySelector('[data-pdp-scroll]');
    const pageScrollCovers = () => pageScroll && getComputedStyle(pageScroll).position === 'fixed';

    let glideRaf = 0;
    const glide = (carAxis, target) => {
      const prop = carAxis === 'x' ? 'scrollLeft' : 'scrollTop';
      const start = view[prop];
      const delta = target - start;
      cancelAnimationFrame(glideRaf);
      if (Math.abs(delta) < 1) return;
      const t0 = performance.now();
      const D = 700; // the scene's own pace, not the browser's sprint
      const step = (now) => {
        const p = Math.min(1, (now - t0) / D);
        view[prop] = start + delta * (1 - Math.pow(1 - p, 4));
        if (p < 1) glideRaf = requestAnimationFrame(step);
      };
      glideRaf = requestAnimationFrame(step);
    };

    const settleSlides = (carAxis, dir) => {
      const size = carAxis === 'x' ? view.clientWidth : view.clientHeight;
      const pos = carAxis === 'x' ? view.scrollLeft : view.scrollTop;
      const eye = pos + size / 2;
      let nearest = 0;
      let nearestDist = Infinity;
      for (let i = 0; i < metrics.length; i += 1) {
        const dist = Math.abs(metrics[i].center - eye);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }
      }
      if (nearestDist < 2) return; // already standing on a centre
      const tolerance = size * 0.08;
      let idx = nearest;
      if (dir > 0 && metrics[nearest].center < eye - tolerance) idx = Math.min(nearest + 1, metrics.length - 1);
      else if (dir < 0 && metrics[nearest].center > eye + tolerance) idx = Math.max(nearest - 1, 0);
      glide(carAxis, metrics[idx].center - size / 2);
    };

    const makeBridge = (carAxis) => {
      if (!pageScrollCovers()) return () => {};
      const prop = carAxis === 'x' ? 'scrollLeft' : 'scrollTop';
      let active = false;
      let axis = '';
      let sx = 0;
      let sy = 0;
      let last = 0;
      let total = 0;
      const onStart = (e) => {
        const t = e.touches[0];
        cancelAnimationFrame(glideRaf);
        active = !(e.target instanceof Element && e.target.closest('.pdp__info, .pdp__dots'));
        axis = '';
        sx = t.clientX;
        sy = t.clientY;
        last = carAxis === 'x' ? t.clientX : t.clientY;
        total = 0;
      };
      const onMove = (e) => {
        if (!active) return;
        const t = e.touches[0];
        if (!axis) {
          const dx = Math.abs(t.clientX - sx);
          const dy = Math.abs(t.clientY - sy);
          if (dx < 6 && dy < 6) return;
          axis = dx > dy ? 'x' : 'y';
        }
        if (axis !== carAxis) return; // the other axis stays native (the card)
        // a vertical carousel shares its axis with the card — steal the pan
        if (carAxis === 'y' && e.cancelable) e.preventDefault();
        const p = carAxis === 'x' ? t.clientX : t.clientY;
        const d = p - last;
        view[prop] -= d;
        total += d;
        last = p;
      };
      const onEnd = () => {
        // finger travelling up/left drags the content forward
        if (active && axis === carAxis) settleSlides(carAxis, total < -45 ? 1 : total > 45 ? -1 : 0);
        active = false;
      };
      pageScroll.addEventListener('touchstart', onStart, { passive: true });
      pageScroll.addEventListener('touchmove', onMove, { passive: false });
      pageScroll.addEventListener('touchend', onEnd, { passive: true });
      return () => {
        pageScroll.removeEventListener('touchstart', onStart);
        pageScroll.removeEventListener('touchmove', onMove);
        pageScroll.removeEventListener('touchend', onEnd);
      };
    };

    const mark = (index) => {
      if (index === current) return;
      current = index;
      for (const c of controls) {
        c.setAttribute('aria-current', Number(c.dataset.pdpGoto) === index ? 'true' : 'false');
      }
    };

    const paint = (m, blur, scale) => {
      if (blur === m.blur && scale === m.scale) return;
      m.blur = blur;
      m.scale = scale;
      if (!m.img) return;
      m.img.style.filter = blur === 0 ? 'none' : `blur(${blur}px)`;
      m.img.style.transform = scale === 1 ? 'translateZ(0)' : `translateZ(0) scale(${scale})`;
    };

    const clearPaint = () => {
      for (const m of metrics) {
        m.blur = -1;
        m.scale = -1;
        m.img?.style.removeProperty('filter');
        m.img?.style.removeProperty('transform');
      }
    };

    let teardownBody = () => {};
    let currentAxis = '';

    /* ---- the hand body: horizontal, native scroll, skylrk's pull ---- */
    const buildHorizontal = () => {
      let viewWidth = 1;
      let step = 1;

      const measure = () => {
        viewWidth = view.clientWidth || 1;
        for (const m of metrics) m.center = m.slide.offsetLeft + m.slide.offsetWidth / 2;
        step = metrics.length > 1 ? Math.max(1, metrics[1].center - metrics[0].center) : viewWidth;
      };

      const onScroll = () => {
        const eye = view.scrollLeft + viewWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < metrics.length; i += 1) {
          const dist = Math.abs(metrics[i].center - eye);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
          if (motionAllowed()) {
            const l = 1 - (dist / step) * SK_PULL;
            const scale = Math.min(Math.max(Math.round((l / SCALE_STEP)) * SCALE_STEP, SK_SCALE_MIN), 1);
            const blur = Math.min(Math.max(Math.round(((1 - l) * 15) / BLUR_STEP) * BLUR_STEP, 0), SK_BLUR_MAX);
            paint(metrics[i], blur <= 0.1 ? 0 : blur, scale);
          }
        }
        mark(nearest);
      };

      const go = (event) => {
        const slide = slides[Number(event.currentTarget.dataset.pdpGoto) || 0];
        if (!slide) return;
        view.scrollTo({ left: slide.offsetLeft - (viewWidth - slide.offsetWidth) / 2, behavior: 'smooth' });
      };

      measure();
      onScroll();
      view.addEventListener('scroll', onScroll, { passive: true });
      controls.forEach((c) => c.addEventListener('click', go));

      const unbridge = makeBridge('x');

      /* iPads (and any direct-touch scroller): the column scrolls natively;
         when the momentum dies, settle directionally — same law as
         everywhere, never backward. */
      let idleTimer = 0;
      let lastLeft = view.scrollLeft;
      let scrollDir = 0;
      let touchingView = false;
      const onViewTouchStart = () => {
        touchingView = true;
        clearTimeout(idleTimer);
        cancelAnimationFrame(glideRaf);
      };
      const onViewTouchEnd = () => {
        touchingView = false;
      };
      const onIdleScroll = () => {
        const left = view.scrollLeft;
        if (left !== lastLeft) scrollDir = left > lastLeft ? 1 : -1;
        lastLeft = left;
        clearTimeout(idleTimer);
        if (!touchingView) idleTimer = setTimeout(() => settleSlides('x', scrollDir), 150);
      };
      if (!pageScrollCovers()) {
        view.addEventListener('touchstart', onViewTouchStart, { passive: true });
        view.addEventListener('touchend', onViewTouchEnd, { passive: true });
        view.addEventListener('scroll', onIdleScroll, { passive: true });
      }

      return {
        remeasure: () => {
          measure();
          onScroll();
        },
        onMotionPref: () => {
          if (!motionAllowed()) clearPaint();
          onScroll();
        },
        destroy: () => {
          unbridge();
          clearTimeout(idleTimer);
          view.removeEventListener('touchstart', onViewTouchStart);
          view.removeEventListener('touchend', onViewTouchEnd);
          view.removeEventListener('scroll', onIdleScroll);
          view.removeEventListener('scroll', onScroll);
          controls.forEach((c) => c.removeEventListener('click', go));
        },
      };
    };

    /* ---- the desk body: vertical, Lenis + directional settle ---- */
    const buildVertical = () => {
      let engine = null;
      let viewHeight = 1;

      const measure = () => {
        viewHeight = view.clientHeight || 1;
        for (const m of metrics) m.center = m.slide.offsetTop + m.slide.offsetHeight / 2;
      };

      const onScroll = () => {
        const scroll = engine ? engine.animatedScroll : view.scrollTop;
        const half = viewHeight / 2 || 1;
        const eye = scroll + half;

        let nearest = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < metrics.length; i += 1) {
          const dist = Math.abs(metrics[i].center - eye);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        }

        if (motionAllowed()) {
          for (let i = 0; i < metrics.length; i += 1) {
            const effect =
              Math.abs(i - nearest) <= NEIGHBOURS
                ? Math.min(1, Math.max(0, (Math.abs(metrics[i].center - eye) / half - DEAD_ZONE) / (1 - DEAD_ZONE)))
                : 1;
            const blur = Math.round((effect * BLUR_MAX) / BLUR_STEP) * BLUR_STEP;
            const scale = 1 - Math.round((effect * SCALE_MAX) / SCALE_STEP) * SCALE_STEP;
            paint(metrics[i], blur, scale);
          }
        }

        mark(nearest);

        if (engine) {
          if (settling !== -1) {
            if (Math.abs(metrics[settling].center - eye) < 1) settling = -1;
          } else if (performance.now() - lastInput > 140 && Math.abs(engine.velocity) < 18 && nearestDist > 2) {
            const dir = engine.direction || 0;
            const tolerance = viewHeight * 0.06;
            let idx = nearest;
            if (dir > 0 && metrics[nearest].center < eye - tolerance) idx = Math.min(nearest + 1, metrics.length - 1);
            else if (dir < 0 && metrics[nearest].center > eye + tolerance) idx = Math.max(nearest - 1, 0);
            settling = idx;
            engine.scrollTo(metrics[idx].center - half, { lerp: 0.09 });
          }
        }
      };

      // On a landscape hand the page scroller covers the photos, so Lenis
      // would never feel a touch — the bridge drives the column natively.
      const covered = pageScrollCovers();
      if (typeof Lenis !== 'undefined' && !covered) {
        engine = new Lenis({
          wrapper: view,
          content: strip,
          syncTouch: true,
          syncTouchLerp: 0.075,
          touchInertiaExponent: 1.7,
          smoothWheel: true,
          lerp: config.scrollLerp || 0.1,
          gestureOrientation: 'vertical',
          overscroll: false,
          autoRaf: true,
        });
      }
      const unbridge = covered ? makeBridge('y') : () => {};

      /* Directional settle for the wheel (desks) — the same law as every
         snap on the site: once input stops and the glide decays, finish
         forward on the next centre, never backward. */
      let settling = -1;
      let lastInput = 0;
      const onInput = () => {
        lastInput = performance.now();
        settling = -1;
      };
      if (engine) {
        view.addEventListener('wheel', onInput, { passive: true });
        view.addEventListener('touchstart', onInput, { passive: true });
      }

      const go = (event) => {
        const slide = slides[Number(event.currentTarget.dataset.pdpGoto) || 0];
        if (!slide) return;
        const offset = -(viewHeight - slide.offsetHeight) / 2;
        if (engine) engine.scrollTo(slide, { offset });
        else view.scrollTo({ top: slide.offsetTop + offset, behavior: 'smooth' });
      };

      measure();
      if (engine) engine.on('scroll', onScroll);
      else view.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      controls.forEach((c) => c.addEventListener('click', go));

      return {
        remeasure: () => {
          measure();
          onScroll();
        },
        onMotionPref: () => {
          if (!motionAllowed()) clearPaint();
          onScroll();
        },
        destroy: () => {
          unbridge();
          view.removeEventListener('wheel', onInput);
          view.removeEventListener('touchstart', onInput);
          controls.forEach((c) => c.removeEventListener('click', go));
          if (engine) {
            engine.off('scroll', onScroll);
            engine.destroy();
          } else {
            view.removeEventListener('scroll', onScroll);
          }
        },
      };
    };

    let body = null;

    const build = () => {
      currentAxis = getComputedStyle(strip).flexDirection === 'column' ? 'y' : 'x';
      body = currentAxis === 'y' ? buildVertical() : buildHorizontal();
      teardownBody = body.destroy;
    };

    build();

    const onResize = () => {
      const axis = getComputedStyle(strip).flexDirection === 'column' ? 'y' : 'x';
      if (axis !== currentAxis) {
        teardownBody();
        clearPaint();
        build();
      } else {
        body.remeasure();
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    // snap stays either way; blur and scale are decoration and must let go
    const onMotionPref = () => body.onMotionPref();
    reducedMotion.addEventListener('change', onMotionPref);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      reducedMotion.removeEventListener('change', onMotionPref);
      teardownBody();
      clearPaint();
    };
  }

  /* -------------------------------------------------------------------
   * Gradient shift — the world takes the color of the attended object.
   * Desktop: hover. Touch: the snap world drives it instead (above).
   * ----------------------------------------------------------------- */
  function initGradientShift(section, { skipTouch = false } = {}) {
    let atmosphere = section.querySelector('.atmosphere');
    // a second museum section's own atmosphere is CSS-hidden (one world per
    // page) — its hovers recolour the page's first, visible one instead
    if (atmosphere && getComputedStyle(atmosphere).display === 'none') {
      atmosphere = document.querySelector('.atmosphere');
    }
    if (!atmosphere) return () => {};
    // A live snap world owns the atmosphere outright — even a hover-capable
    // device (an iPad with a trackpad) must not fight it with hover rests.
    if (skipTouch) return () => {};

    const tiles = [...section.querySelectorAll('[data-gradient-start]')];
    const rest = () => {
      atmosphere.style.removeProperty('--g1');
      atmosphere.style.removeProperty('--g2');
      restClouds(atmosphere);
    };
    const shiftTo = (tile) => {
      atmosphere.style.setProperty('--g1', tile.dataset.gradientStart);
      atmosphere.style.setProperty('--g2', tile.dataset.gradientEnd || tile.dataset.gradientStart);
      paintClouds(atmosphere, tileClouds(tile));
    };

    const cleanups = [];

    if (hoverCapable.matches) {
      for (const tile of tiles) {
        const enter = () => shiftTo(tile);
        tile.addEventListener('mouseenter', enter);
        tile.addEventListener('focusin', enter);
        tile.addEventListener('mouseleave', rest);
        tile.addEventListener('focusout', rest);
        cleanups.push(() => {
          tile.removeEventListener('mouseenter', enter);
          tile.removeEventListener('focusin', enter);
          tile.removeEventListener('mouseleave', rest);
          tile.removeEventListener('focusout', rest);
        });
      }
    } else {
      // Touch without a snap world (any template but the homepage): shift to
      // the tile crossing the center band of the viewport.
      const inBand = new Set();
      const centerObserver = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) inBand.add(e.target);
            else inBand.delete(e.target);
          }
          const hit = entries.find((e) => e.isIntersecting);
          if (hit) shiftTo(hit.target);
          else if (!inBand.size) rest();
        },
        { rootMargin: '-40% 0% -40% 0%' }
      );
      tiles.forEach((t) => centerObserver.observe(t));
      cleanups.push(() => centerObserver.disconnect());
    }

    return () => {
      cleanups.forEach((fn) => fn());
      rest();
    };
  }

  /* -------------------------------------------------------------------
   * Reveals — once, staggered, never re-hidden
   * ----------------------------------------------------------------- */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = Number(e.target.dataset.revealIndex || 0);
        e.target.style.transitionDelay = `${(i % 6) * 70}ms`;
        e.target.classList.add('is-in');
        revealObserver.unobserve(e.target);
      });
    },
    { threshold: 0.15 }
  );

  /* -------------------------------------------------------------------
   * The chrome — the menu tablet, and the policy accordion on its own page.
   *
   * Wired by delegation on the document, not by walking the header on load:
   * the theme editor re-renders that section constantly, and delegation
   * cannot come apart when it does.
   * ----------------------------------------------------------------- */
  const menuPanel = () => document.getElementById('site-menu');
  const menuToggle = () => document.querySelector('[data-menu-toggle]');

  function setMenu(open) {
    const panel = menuPanel();
    const toggle = menuToggle();
    if (!panel) return;
    if (open) panel.setAttribute('data-open', '');
    else panel.removeAttribute('data-open');
    panel.inert = !open;
    if (toggle) {
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = open ? toggle.dataset.labelOpen : toggle.dataset.labelClosed;
      if (label) toggle.setAttribute('aria-label', label);
    }
    document.documentElement.classList.toggle('nav-open', open);
    if (open) {
      // one frame so the sheet is actually visible before we try to focus into it
      requestAnimationFrame(() => panel.querySelector('a')?.focus({ preventScroll: true }));
    } else if (toggle?.offsetParent) {
      toggle.focus({ preventScroll: true });
    }
  }

  function collapseAllPolicies() {
    document.querySelectorAll('[data-policy-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      const panel = button.closest('[data-policy]')?.querySelector('[data-policy-panel]');
      if (panel) {
        panel.removeAttribute('data-open');
        panel.inert = true;
      }
    });
  }

  function togglePolicy(button) {
    const panel = button.closest('[data-policy]')?.querySelector('[data-policy-panel]');
    if (!panel) return;
    const willOpen = button.getAttribute('aria-expanded') !== 'true';
    // one policy open at a time — a stack of open drawers is not a document
    collapseAllPolicies();
    if (!willOpen) return;
    button.setAttribute('aria-expanded', 'true');
    panel.setAttribute('data-open', '');
    panel.inert = false;
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // The menu is a floating tablet, not an overlay — a click anywhere
    // outside it (and outside its toggles) puts it away.
    if (
      menuPanel()?.hasAttribute('data-open') &&
      !target.closest('#site-menu') &&
      !target.closest('[data-menu-toggle]')
    ) {
      setMenu(false);
    }

    if (target.closest('[data-menu-toggle]')) {
      setMenu(!menuPanel()?.hasAttribute('data-open'));
      return;
    }

    const bar = target.closest('[data-policy-toggle]');
    if (bar) togglePolicy(bar);
  });

  document.addEventListener('keydown', (event) => {
    // the tablet is not a dialog — the bar's own lines have to stay above it
    // and clickable — so Escape is ours to handle
    if (event.key !== 'Escape') return;
    if (menuPanel()?.hasAttribute('data-open')) setMenu(false);
  });

  phoneLandscape.addEventListener('change', (event) => {
    if (event.matches) setMenu(false);
  });

  /* Policies start folded, whatever the browser restored on a back-navigation. */
  function initChrome() {
    collapseAllPolicies();
  }

  /* -------------------------------------------------------------------
   * Section wiring — works on live pages and inside the theme editor
   * ----------------------------------------------------------------- */
  const sectionCleanups = new Map();

  function initSection(root) {
    // The theme editor can re-run this on the same root; without this the
    // previous cleanup function is dropped and observers/drifters leak.
    if (sectionCleanups.has(root)) teardownSection(root);

    const cleanup = [];

    root.querySelectorAll('[data-drift]').forEach((el) => {
      addDrifter(el);
      cleanup.push(() => removeDrifter(el));
    });

    // Arm reveals only when this engine is alive — no JS, no hiding
    const reveals = root.querySelectorAll('.reveal');
    if (reveals.length && !reducedMotion.matches) {
      root.classList.add('reveals-armed');
    }
    reveals.forEach((el, i) => {
      if (reducedMotion.matches) {
        el.classList.add('is-in');
        return;
      }
      el.dataset.revealIndex = i;
      revealObserver.observe(el);
      cleanup.push(() => revealObserver.unobserve(el));
    });

    const world = initSnapWorld(root);
    if (world) cleanup.push(world);

    cleanup.push(initPdpCarousel(root));

    if (root.querySelector('.atmosphere')) {
      cleanup.push(initGradientShift(root, { skipTouch: Boolean(world) }));
    }

    sectionCleanups.set(root, () => cleanup.forEach((fn) => fn()));
  }

  function teardownSection(root) {
    sectionCleanups.get(root)?.();
    sectionCleanups.delete(root);
  }

  function boot() {
    startLenis();
    initChrome();
    document.querySelectorAll('[data-loxord-section]').forEach(initSection);
  }

  document.addEventListener('shopify:section:load', (e) => {
    initChrome();
    const root = e.target.querySelector('[data-loxord-section]');
    if (root) initSection(root);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-loxord-section]');
    if (root) teardownSection(root);
  });

  // Without this a merchant cannot select objects 2–5 in a mobile preview:
  // they are simply off screen.
  document.addEventListener('shopify:block:select', (e) => {
    const cell = e.target.closest?.('.fmg__item');
    const root = cell?.closest('[data-loxord-section]');
    const world = root && snapWorlds.get(root);
    if (world) world.focusCell(cell);
  });

  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      stopLenis();
      stopDrift();
    } else {
      startLenis();
      if (config.enabled && drifters.size) {
        driftRunning = true;
        requestAnimationFrame(driftLoop);
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
